/* eslint-disable max-lines */
import { useContext, useCallback } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import {
    impressionSuperNudge,
    impressionOneTapAddNudge,
    impressionSuperChangePlan,
    impressionSuperOfferBadge,
    impressionSuperRemovePlanConfirmationHalfCard,
    impressionSuperRemovePlanConfirmationBackButton,
    impressionSuperRemovePlanConfirmationRemoveButton,
    clickSuperRemovePlanHalfCardBackButton,
    clickSuperRemovePlanHalfCardRemoveButton,
    impressionSuperViewDetailsFooter,
    clickSuperViewDetailsFooterCTA,
    impressionSuperViewAllBenefitsCTA,
    clickSuperViewAllBenefitsCTA,
    clickRemoveSuper,
    clickSwiggyOneEOCompletioNudge,
    impressionSwiggyOneEOCompletioNudge,
    impressionSwiggyOneSavingsNudge,
    impressionFooterStrip,
} from "@cart-client/analytics/events";
import {
    Benefit,
    RenderingDetails,
    UserSuperStatus,
    RemovePlanConfirmationHalfCard,
    RemovePlanConfirmationHalfCardButton,
} from "@swiggy-private/cart-types";
import { TooltipContext } from "@swiggy-private/super-ui";

export function useSuperAnalytics(userSuperStatus: UserSuperStatus | string) {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const onImpressionSuperNudge = useCallback(
        (title: string, ctaText: string, subtitle = "", link = "") => {
            impressionSuperNudge({ title, ctaText, subtitle, userSuperStatus, link }, impressionEvent);
        },
        [impressionEvent, userSuperStatus],
    );

    const onImpressionSuperOneTapAddNudge = useCallback(
        (data: {
            title: string;
            ctaText: string;
            planId: string;
            planName: string;
            planPrice: string;
            subtitle?: string;
        }) => {
            const payload = {
                ...data,
                subtitle: data.subtitle || "",
                userSuperStatus,
            };
            impressionOneTapAddNudge(payload, impressionEvent);
        },
        [impressionEvent, userSuperStatus],
    );

    const onImpressionSuperChangePlan = useCallback(
        (planName: string, ctaLink: string, planPrice: string, itemData: RenderingDetails) => {
            const { meta } = itemData;
            const { benefits = [] } = meta || {};
            impressionSuperChangePlan(
                {
                    planName,
                    planId: meta?.plan_details?.plan_id || "",
                    userSuperStatus,
                    planPrice,
                    benefits: JSON.stringify(benefits),
                    ctaLink,
                },
                impressionEvent,
            );
        },
        [impressionEvent, userSuperStatus],
    );

    const onImpressionSuperOfferBadge = useCallback(
        (offerText: string) => {
            impressionSuperOfferBadge(offerText, userSuperStatus, impressionEvent);
        },
        [impressionEvent, userSuperStatus],
    );

    const onImpressionSuperRemovePlanConfirmationHalfCard = useCallback(
        ({
            savingsValue,
            title,
            message,
            subMessage,
            planId,
            variant,
            initialBillAmount,
            finalBillAmount,
        }: RemovePlanConfirmationHalfCard) => {
            impressionSuperRemovePlanConfirmationHalfCard(
                {
                    savingsValue,
                    userSuperStatus,
                    title,
                    message,
                    subMessage,
                    planId,
                    variant,
                    initialBillAmount,
                    finalBillAmount,
                },
                impressionEvent,
            );
        },
        [impressionEvent, userSuperStatus],
    );

    const onImpressionSuperRemovePlanHalfCardBackButton = useCallback(
        ({
            planName,
            planId,
            cta,
            variant,
            initialBillAmount,
            finalBillAmount,
        }: RemovePlanConfirmationHalfCardButton) => {
            impressionSuperRemovePlanConfirmationBackButton(
                { userSuperStatus, planName, planId, cta, variant, initialBillAmount, finalBillAmount },
                clickEvent,
            );
        },
        [clickEvent, userSuperStatus],
    );

    const onImpressionSuperRemovePlanHalfCardRemoveButton = useCallback(
        ({
            planName,
            planId,
            cta,
            variant,
            initialBillAmount,
            finalBillAmount,
        }: RemovePlanConfirmationHalfCardButton) => {
            impressionSuperRemovePlanConfirmationRemoveButton(
                { userSuperStatus, planName, planId, cta, variant, initialBillAmount, finalBillAmount },
                clickEvent,
            );
        },
        [clickEvent, userSuperStatus],
    );

    const onClickSuperRemovePlanHalfCardBackButton = useCallback(
        ({
            planName,
            planId,
            cta,
            variant,
            initialBillAmount,
            finalBillAmount,
        }: RemovePlanConfirmationHalfCardButton) => {
            clickSuperRemovePlanHalfCardBackButton(
                { userSuperStatus, planName, planId, cta, variant, initialBillAmount, finalBillAmount },
                clickEvent,
            );
        },
        [clickEvent, userSuperStatus],
    );

    const onClickSuperRemovePlanHalfCardRemoveButton = useCallback(
        ({
            planName,
            planId,
            cta,
            variant,
            initialBillAmount,
            finalBillAmount,
        }: RemovePlanConfirmationHalfCardButton) => {
            clickSuperRemovePlanHalfCardRemoveButton(
                { userSuperStatus, planName, planId, cta, variant, initialBillAmount, finalBillAmount },
                clickEvent,
            );
        },
        [clickEvent, userSuperStatus],
    );

    const onClickRemoveSuper = useCallback(
        (planId: string, planName: string, planPrice: string) => {
            clickRemoveSuper({ planId, userSuperStatus, planPrice, planName }, clickEvent);
        },
        [clickEvent, userSuperStatus],
    );

    const onImpressionSuperViewDetailsFooterWidget = useCallback(
        (ctaText: string) => {
            impressionSuperViewDetailsFooter(ctaText, impressionEvent);
        },
        [impressionEvent],
    );

    const onClickSuperViewDetailsFooterWidget = useCallback(
        (ctaText: string) => {
            clickSuperViewDetailsFooterCTA(ctaText, clickEvent);
        },
        [clickEvent],
    );

    const onImpressionSuperViewAllBenefitsCTA = useCallback(
        (ctaText: string, ctaLink: string, toolTipContext?: TooltipContext) => {
            const contextData = { ctaText, ctaLink, ...toolTipContext };
            impressionSuperViewAllBenefitsCTA(userSuperStatus, contextData, impressionEvent);
        },
        [impressionEvent, userSuperStatus],
    );

    const onClickSuperViewAllBenefitsCTA = useCallback(
        (
            ctaText: string,
            ctaLink: string,
            toolTipContext?: TooltipContext,
            planDetails?: {
                planId?: string;
                planName?: string;
                planPrice?: string;
            },
        ) => {
            const contextData = { ctaText, ctaLink, ...toolTipContext, ...planDetails };
            clickSuperViewAllBenefitsCTA(userSuperStatus, contextData, clickEvent);
        },
        [clickEvent, userSuperStatus],
    );
    // eo analytics events <---->
    const onImpressionSwiggyOneSavingsNudge = useCallback(
        (benefits: Benefit, totalSavings?: string) => {
            impressionSwiggyOneSavingsNudge(impressionEvent, JSON.stringify({ benefits }), totalSavings);
        },
        [impressionEvent],
    );

    const onImpressionSwiggyOneEOCompletionNudge = useCallback(
        (benefits: Benefit, restaurantId) => {
            if (benefits && restaurantId) {
                impressionSwiggyOneEOCompletioNudge(impressionEvent, JSON.stringify(benefits), restaurantId);
            }
        },
        [impressionEvent],
    );

    const onClickSwiggyOneEOCompletioNudge = useCallback(
        (benefits: Benefit, restaurantId) => {
            clickSwiggyOneEOCompletioNudge(clickEvent, JSON.stringify(benefits), restaurantId);
        },
        [clickEvent],
    );
    // eo analytics events <---->

    const onFooterStripImpression = useCallback(
        (title: string, ctaText: string) => impressionFooterStrip({ userSuperStatus, title, ctaText }, impressionEvent),
        [impressionEvent, userSuperStatus],
    );

    return {
        onImpressionSuperNudge,
        onImpressionSuperOneTapAddNudge,
        onImpressionSuperChangePlan,
        onImpressionSuperOfferBadge,
        onImpressionSuperRemovePlanConfirmationHalfCard,
        onImpressionSuperRemovePlanHalfCardBackButton,
        onImpressionSuperRemovePlanHalfCardRemoveButton,
        onClickSuperRemovePlanHalfCardBackButton,
        onClickSuperRemovePlanHalfCardRemoveButton,
        onClickRemoveSuper,
        onImpressionSuperViewDetailsFooterWidget,
        onClickSuperViewDetailsFooterWidget,
        onImpressionSuperViewAllBenefitsCTA,
        onClickSuperViewAllBenefitsCTA,
        onImpressionSwiggyOneSavingsNudge,
        onImpressionSwiggyOneEOCompletionNudge,
        onClickSwiggyOneEOCompletioNudge,
        onFooterStripImpression,
    };
}
